import { IonButton } from '@ionic/react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useDeliveryStatusByMark } from '../../api/hooks/delivery/useDeliveryStatusByMark';
import { Loading } from '../../components/atoms/Loading';
import { STANDALONE_FEATURE } from '../../interfaces/enumerables';
import { PATHS } from '../../navigation/data';
import {
  canConfirmOutcome,
  canRegisterTransfer,
  getConfirmOutcomeButtonCopy,
} from '../../services/utils/deliveryStatusUtils';
import { useHasActiveFeature } from '../../services/utils/useHasActiveFeature';
import { DeliveryInstructionsLink } from './component/DeliveryInstructionsLink';
import { DeliveryRegisterTransferForm } from './component/DeliveryRegisterTransferForm';
import { DeliveryStatusGsisDoc } from './component/DeliveryStatusGsisDoc';
import { DeliveryStatusSteps } from './component/DeliveryStatusSteps';
import { ProviderInvoiceDoc } from './component/ProviderInvoiceDoc';

export const NewDeliveryRegisterTransferScreen = () => {
  const { isChecking: isFeatureChecking } = useHasActiveFeature(STANDALONE_FEATURE.TRANSIT, true);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mark } = useParams<{ mark: string }>();
  const isCreate = useMemo(() => mark === 'create', [mark]);
  const { data, isLoading, isRefetching } = useDeliveryStatusByMark(mark);

  const getLoading = useCallback(() => {
    if (isLoading || isRefetching || isFeatureChecking) {
      return <Loading backgroundFull />;
    }

    return null;
  }, [isLoading, isRefetching, isFeatureChecking]);

  const getErrorCode = useCallback(() => {
    if (data?.errorCode && !data?.extraData?.doc) {
      return (
        <div className="m-t-16 m-b-16 flex-center-center-column-nowrap width-100">
          <div className="boldText4 text-center text-danger text-with-new-line max-width-900">
            {t(`API_ERRORS.${data?.errorCode}`)}
          </div>
        </div>
      );
    }

    return null;
  }, [data?.errorCode, data?.extraData?.doc, t]);

  const getCurrentStatus = useCallback(() => {
    if (data?.data) {
      return (
        <div className="margin-b-16 flex-center-center-column-nowrap width-100">
          <div className="boldText6">{t('DELIVERY.DELIVERY_STATUS')}</div>
          <div className="margin-t-16 simpleBorder p-12">
            <div className="normalText5 m-b-6">
              <b>{t('DELIVERY.NEW_DELIVERY_MARK_PLACEHOLDER')}</b>: {data?.data?.invoiceMark}
            </div>
            <div className="normalText5">
              <b>{t('DELIVERY.DELIVERY_STATUS_CURRENT')}</b>: {t(`DELIVERY.STATUS.${data?.data?.status}`)}
            </div>
          </div>
        </div>
      );
    }
  }, [data?.data, t]);

  const getGsisDocData = useCallback(() => {
    if (data?.extraData?.doc) {
      return (
        <div className="m-b-32 flex-center-center-column-nowrap width-100">
          <DeliveryStatusGsisDoc doc={data?.extraData?.doc} />
        </div>
      );
    }
    return null;
  }, [data?.extraData?.doc]);

  const getProviderInvoiceData = useCallback(() => {
    const qrUrl = data?.extraData?.doc?.qrCodeUrl;
    if (!qrUrl) return null;
    return (
      <div className="m-b-32 flex-center-center-column-nowrap width-100">
        <ProviderInvoiceDoc qrUrl={qrUrl} />
      </div>
    );
  }, [data?.extraData?.doc?.qrCodeUrl]);

  const getActionForm = useCallback(() => {
    if (canRegisterTransfer(data?.data, data?.extraData?.doc)) {
      return (
        <div className="m-b-32 flex-center-center-column-nowrap width-100">
          <div className="flex-center-center-column-nowrap">
            <DeliveryRegisterTransferForm
              mark={data?.extraData?.doc?.mark || ''}
              qrUrl={data?.extraData?.doc?.qrCodeUrl || ''}
            />
          </div>
        </div>
      );
    }
  }, [data?.data, data?.extraData?.doc]);

  const getSteps = useCallback(() => {
    if (data?.data?.lifecycleHistory) {
      return (
        <div className="m-b-32 flex-center-center-column-nowrap width-100">
          <DeliveryStatusSteps lifecycleHistory={data?.data?.lifecycleHistory} />
        </div>
      );
    }
  }, [data?.data?.lifecycleHistory]);

  const getConfirmOutcomeSection = useCallback(() => {
    if (canConfirmOutcome(data?.extraData?.isIssuer, data?.data, data?.extraData?.doc) && !isCreate) {
      const goToConfirmDelivery = () => navigate(`${PATHS.DELIVERIES}/confirm-outcome/${data?.extraData?.doc?.mark}`);

      const label = getConfirmOutcomeButtonCopy(data?.extraData?.isIssuer);

      return (
        <div className="width-100 flex-center-center-column-nowrap m-b-16">
          <IonButton mode="md" className="width-350px ion-info-button" fill="outline" onClick={goToConfirmDelivery}>
            {t(`DELIVERY.${label}`)}
          </IonButton>
        </div>
      );
    }
  }, [data?.data, data?.extraData?.doc, data?.extraData?.isIssuer, isCreate, navigate, t]);

  const getMainContent = useCallback(() => {
    if (isLoading) return;

    return (
      <div className="flex-center-start-column-nowrap width-100">
        {getErrorCode()}
        {getCurrentStatus()}
        {getConfirmOutcomeSection()}
        {getActionForm()}
        {getSteps()}
        {getGsisDocData()}
        {getProviderInvoiceData()}
      </div>
    );
  }, [getActionForm, getConfirmOutcomeSection, getCurrentStatus, getErrorCode, getGsisDocData, getSteps, isLoading]);

  return (
    <div className="scrollable">
      {getLoading()}
      <div className="width-100 p-t-16 p-b-16 flex-center-start-column-nowrap p-r-24 p-l-24">
        <DeliveryInstructionsLink />
        {getMainContent()}
      </div>
    </div>
  );
};
